import i18n, { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LANGUAGE_CODES, SOURCE_LANGUAGE, langDef } from './languages';

export type { AppLanguage } from './languages';

// The Turkish text is the key: t('Çıkış Yap'). A key with no translation renders as itself, so
// Turkish needs no dictionary and can never show a missing key. Every other language has a
// folder locales/<code>, fetched as its own chunk the first time someone switches to it.
const lazyLocales: BackendModule = {
  type: 'backend',
  init() {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    if (language === SOURCE_LANGUAGE) { callback(null, {}); return; }
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${language}/index.ts`)
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err, null));
  },
};

i18n
  .use(lazyLocales)
  .use(initReactI18next)
  .init({
    lng: 'tr',
    // Resolve the start language synchronously (the backend answers 'tr' at once), so the
    // first render already has it and LanguageBoundary never remounts a Turkish session
    initImmediate: false,
    // A missing translation tries the language's fallbacks, then shows the Turkish key itself
    fallbackLng: (code: string) => langDef(code).fallback,
    supportedLngs: LANGUAGE_CODES,
    load: 'currentOnly',
    ns: ['translation'],
    defaultNS: 'translation',
    // Natural-language keys contain '.' and ':' — never split them
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    returnEmptyString: false,
    // A component outside <Suspense> must not suspend; until English has loaded it shows Turkish
    react: { useSuspense: false },
  });

/** Marks a Turkish string for translation without translating it here (for constant tables
 *  that are translated where they are displayed, e.g. t(STATUS_LABELS[s])). */
export const N_ = (text: string): string => text;

/**
 * Turns the given string fields of a constant table (arrays/objects, searched recursively) into
 * getters that translate on every read. Tables built once at module load then follow the UI
 * language without touching the code that reads them. Mark the source strings with N_().
 */
export function localized<T extends object>(table: T, fields: string[] = ['label'], options?: Record<string, unknown>): T {
  const visit = (node: any) => {
    if (Array.isArray(node)) { node.forEach(visit); return; }
    if (!node || typeof node !== 'object') return;
    for (const key of Object.keys(node)) {
      const value = node[key];
      if (fields.includes(key) && typeof value === 'string') {
        Object.defineProperty(node, key, { get: () => i18n.t(value, options), enumerable: true, configurable: true });
      } else if (value && typeof value === 'object') {
        visit(value);
      }
    }
  };
  visit(table);
  return table;
}

/** Like localized() for a list of plain strings (e.g. weekday names): items translate on read */
export function localizedList<T extends string[]>(list: T): T {
  list.forEach((value, index) => {
    Object.defineProperty(list, index, { get: () => i18n.t(value), enumerable: true, configurable: true });
  });
  return list;
}

/** Like localized() for a key → label record: every value translates on read */
export function localizedRecord<T extends Record<string, string>>(record: T): T {
  for (const key of Object.keys(record)) {
    const value = record[key];
    Object.defineProperty(record, key, { get: () => i18n.t(value), enumerable: true, configurable: true });
  }
  return record;
}

export default i18n;
